import * as Yup from 'yup';

import { ProjectCompetitor } from '@core/types/api/ProjectCompetitor/ProjectCompetitor';
import { QuoteCompetitor } from '@core/types/api/QuoteCompetitor/QuoteCompetitor';
import { hasCompetitorLossDataFromFormik } from '@core/utils/competitors';

import { NewCurrency } from '../types/api/Currency/Currency';
import { FormikCurrency } from '../utils/formikHelpers';
import { ParseNullableNumericString } from '../utils/formParsingHelpers';
import { NIL_UUID, isValidUUID } from '../utils/uuid';
import {
  YupNullableCurrency,
  YupNullableReference,
  YupNullableString,
  YupReference,
  type YupSchemaNullableCurrencyType,
  type YupSchemaNullableReferenceType,
  type YupSchemaReferenceType,
  nonNegativeCurrency,
} from '../utils/yupHelpers';

export type ProjectOrQuoteCompetitorProductFormikType = {
  product: YupSchemaReferenceType;
  price: YupSchemaNullableCurrencyType;
};

export type CompetitorLossFormikType = {
  lossReason: YupSchemaNullableReferenceType;
  lossReasonNotes: string | null;
  competitor: YupSchemaNullableReferenceType;
  competitorProducts: ProjectOrQuoteCompetitorProductFormikType[];
};

export const competitorLossFormikSchemaFields = {
  lossReason: YupNullableReference('Loss Reason'),
  lossReasonNotes: YupNullableString('Additional notes'),
  competitor: YupNullableReference('Competitor'),
  competitorProducts: Yup.array().of(
    Yup.object({
      product: YupReference('Competitor Product'),
      price: YupNullableCurrency('Price').test(nonNegativeCurrency),
    }),
  ),
};

/** The competitor/loss data shared by QuoteCompetitor and ProjectCompetitor; each field is tied
 * to both types, so a divergence in the parts the converters read fails to compile here. */
export type CompetitorLossData = {
  competitor: QuoteCompetitor['competitor'] & ProjectCompetitor['competitor'];
  lossReason: Pick<QuoteCompetitor['lossReason'] & ProjectCompetitor['lossReason'], 'id' | 'name'>;
  lossReasonNotes: QuoteCompetitor['lossReasonNotes'] & ProjectCompetitor['lossReasonNotes'];
  products: QuoteCompetitor['products'] & ProjectCompetitor['products'];
};

/** The nested wire competitor object produced from the flattened formik fields. */
export type CompetitorLossWireData = {
  id: string;
  products: {
    id: string;
    price: string | null;
    competitorProduct: { id: string | null; name: string };
  }[];
  competitor: { id: string; name: string } | null;
  lossReason: { id: string; name: string };
  lossReasonNotes: string | null;
};

/**
 * Converts a nested competitor/loss object (as stored on a Quote or Project)
 * into the flattened formik fields.
 */
export const competitorLossToFormik = (
  data: CompetitorLossData | null | undefined,
): CompetitorLossFormikType => {
  const lossReasonId = data?.lossReason?.id ?? null;
  const competitorId = data?.competitor?.id ?? null;
  return {
    lossReason: {
      id: isValidUUID(lossReasonId) ? lossReasonId : null,
      option: isValidUUID(lossReasonId)
        ? { value: lossReasonId, label: data?.lossReason?.name ?? '' }
        : null,
    },
    lossReasonNotes: data?.lossReasonNotes ?? null,
    competitor: {
      id: isValidUUID(competitorId) ? competitorId : null,
      option: isValidUUID(competitorId)
        ? { value: competitorId, label: data?.competitor?.name ?? '' }
        : null,
    },
    competitorProducts: (data?.products ?? []).map((pcp) => ({
      product: {
        id: pcp.competitorProduct.id,
        option: {
          value: pcp.competitorProduct.id,
          label: pcp.competitorProduct.name,
        },
      },
      price: FormikCurrency(
        pcp.price === null ? undefined : NewCurrency({ number: pcp.price, currency: 'USD' }),
      ),
    })),
  };
};

/**
 * Builds the nested wire competitor object from the flattened formik fields.
 * Returns null when there is no loss reason selected (the gating field for
 * persisting competitor data).
 */
export const competitorLossFromFormik = (
  values: CompetitorLossFormikType,
): CompetitorLossWireData | null => {
  const competitorId = values.competitor?.id ?? null;
  const lossReasonId = values.lossReason?.id ?? null;
  if (!hasCompetitorLossDataFromFormik(values)) {
    return null;
  }
  const products = (values.competitorProducts ?? [])
    .filter((cp) => isValidUUID(cp.product.id))
    .map((cp) => ({
      id: NIL_UUID,
      price: ParseNullableNumericString(cp.price?.number ?? null),
      competitorProduct: {
        id: cp.product.id,
        name: cp.product.option?.label ?? '',
      },
    }));
  return {
    id: NIL_UUID,
    products,
    competitor: isValidUUID(competitorId)
      ? { id: competitorId, name: values.competitor?.option?.label ?? '' }
      : null,
    lossReason: {
      id: lossReasonId ?? NIL_UUID,
      name: values.lossReason?.option?.label ?? '',
    },
    lossReasonNotes: values.lossReasonNotes?.trim() || null,
  };
};
